import PropTypes from 'prop-types';
import { NavLink as RouterLink } from 'react-router-dom';
// @mui
import {
    Box,
    Collapse,
    List,
    ListItemText,
    Popover,
    Tooltip
} from '@mui/material';
//
import { StyledNavItem, StyledNavItemIcon } from './styles';
import { useEffect, useRef, useState } from 'react';
import i18n from '../../i18n';

// ----------------------------------------------------------------------

NavSection.propTypes = {
    data: PropTypes.array
};

export default function NavSection({ data = [], collapsed = false, ...other }) {
    const [selectedItem, setSelectedItem] = useState(null);

    return (
        <Box {...other}>
            <List disablePadding sx={{ p: 1 }}>
                {data.map((item) => (
                    <ListItem
                        key={item.title}
                        item={item}
                        collapsed={collapsed}
                        selectedItem={selectedItem}
                        setSelectedItem={setSelectedItem}
                    />
                ))}
            </List>
        </Box>
    );
}

// ----------------------------------------------------------------------
const NestedList = ({ data = [], pl = 4, mrItem, onClosePopover }) => {
    return (
        <List component="div" disablePadding sx={{ pl: pl }}>
            {data.map((item) => (
                <ListItem
                    key={item.title}
                    item={item}
                    mr={mrItem}
                    isNested={true}
                    onClosePopover={onClosePopover}
                />
            ))}
        </List>
    );
};
//----------------------------------------------------------------------

const ListItem = ({
    item,
    mr = 0,
    isNested = false,
    collapsed = false,
    onClosePopover
}) => {
    const { title, path, icon, info, sublistData } = item;
    const [openCollapse, setOpenCollapse] = useState(false);
    const [openPopover, setOpenPopover] = useState(false);
    const itemRef = useRef(null);
    function usePrevious(value) {
        const ref = useRef();
        useEffect(() => {
            ref.current = value;
        }, [value]);
        return ref.current;
    }
    const prevCollapsed = usePrevious(collapsed);

    useEffect(() => {
        if (collapsed && !prevCollapsed) {
            setOpenPopover(false);
        }
    }, [collapsed, prevCollapsed]);

    const translatedTitle = i18n.t(title);

    const handleClick = () => {
        if (sublistData) {
            if (collapsed) {
                setOpenPopover(!openPopover);
            } else {
                setOpenCollapse(!openCollapse);
            }
        }
        onClosePopover?.();
    };

    const ListItemComponent = sublistData ? 'div' : RouterLink;
    const listItemProps = sublistData ? {} : { to: path };

    const content = (
        <StyledNavItem
            component={ListItemComponent}
            onClick={handleClick}
            sx={{
                '&.active': {
                    color: 'text.primary',
                    bgcolor: 'action.selected',
                    fontWeight: 'fontWeightBold'
                }
            }}
            {...listItemProps}
        >
            <StyledNavItemIcon>{icon && icon}</StyledNavItemIcon>

            {!collapsed && (
                <ListItemText
                    disableTypography
                    primary={translatedTitle}
                    sx={{ ml: 1, mr: mr }}
                />
            )}

            {!collapsed && info}
        </StyledNavItem>
    );
    return (
        <>
            <Box ref={itemRef}>
                {collapsed ? (
                    <Tooltip title={translatedTitle} placement="right" arrow>
                        {content}
                    </Tooltip>
                ) : (
                    content
                )}
            </Box>

            {sublistData &&
                (!collapsed ? (
                    <Collapse in={openCollapse} timeout="auto" unmountOnExit>
                        <NestedList data={sublistData} />
                    </Collapse>
                ) : (
                    <Popover
                        open={Boolean(openPopover && itemRef.current)}
                        anchorEl={openPopover ? itemRef.current : null}
                        onClose={() => setOpenPopover(false)}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right'
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left'
                        }}
                        slotProps={{
                            paper: {
                                sx: {
                                    p: 1,
                                    borderRadius: 1.5,
                                    boxShadow: 6,
                                    minWidth: 180
                                }
                            }
                        }}
                    >
                        <Box>
                            <NestedList
                                data={sublistData}
                                pl={0}
                                mrItem={2}
                                onClosePopover={() => setOpenPopover(false)}
                            />
                        </Box>
                    </Popover>
                ))}
        </>
    );
};

ListItem.propTypes = {
    item: PropTypes.object.isRequired,
    isNested: PropTypes.bool
};
